import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe, MapPin, Link as LinkIcon, ChartBar, Users, UserPlus, SignIn } from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";

const PERIODS = [
  { key: "hour", label: "Last Hour" },
  { key: "day", label: "Last 24 Hours" },
  { key: "week", label: "Last 7 Days" },
  { key: "month", label: "Last 30 Days" },
];

function flagFromCode(code) {
  if (!code || code.length !== 2) return "🌐";
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(A + (code.charCodeAt(0) - a)) + String.fromCodePoint(A + (code.charCodeAt(1) - a));
}

export default function AdminVisitors() {
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/visitors?period=${p}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); }, [period]);

  const maxSeries = Math.max(1, ...(data?.series || []).map((s) => s.count));
  const maxCountry = Math.max(1, ...(data?.countries || []).map((c) => c.count));

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1
          className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white"
          style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
        >
          Visitor Analytics
        </h1>
        <div
          data-testid="visitors-period-selector"
          className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1"
        >
          {PERIODS.map((p) => (
            <button
              key={p.key}
              data-testid={`period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
                period === p.key
                  ? "bg-yellow-400 text-yellow-950"
                  : "text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi
          testid="kpi-total-visits"
          icon={ChartBar}
          color="text-blue-500"
          label="Total visits"
          value={data?.total_visits}
        />
        <Kpi
          testid="kpi-unique-visitors"
          icon={Users}
          color="text-green-500"
          label="Unique visitors"
          value={data?.unique_visitors}
        />
        <Kpi
          testid="kpi-signups"
          icon={UserPlus}
          color="text-yellow-500"
          label="New signups"
          value={data?.total_signups}
        />
        <Kpi
          testid="kpi-countries"
          icon={Globe}
          color="text-purple-500"
          label="Countries"
          value={data?.countries?.length}
        />
      </div>

      {loading && !data && (
        <div data-testid="visitors-loading" className="text-sm text-slate-500 mb-4">
          Loading…
        </div>
      )}

      {/* Time series */}
      <div
        data-testid="visitors-timeseries"
        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Visits over time</div>
            <div
              className="text-lg font-black text-slate-900 dark:text-white"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              {PERIODS.find((p) => p.key === period)?.label}
            </div>
          </div>
        </div>

        <div className="flex items-end gap-1 h-40">
          {(data?.series || []).map((s, i) => {
            const h = Math.max(2, (s.count / maxSeries) * 100);
            return (
              <div
                key={i}
                data-testid={`series-bar-${i}`}
                className="flex-1 flex flex-col items-center justify-end group"
                title={`${s.label}: ${s.count}`}
              >
                <div
                  className="w-full rounded-t bg-gradient-to-t from-blue-600 to-lime-400 group-hover:from-blue-700 group-hover:to-yellow-400 transition-colors"
                  style={{ height: `${h}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex justify-between text-[9px] text-slate-500 gap-1 overflow-hidden">
          {(data?.series || []).map((s, i) => (
            <div key={i} className="flex-1 text-center truncate">{s.label}</div>
          ))}
        </div>
      </div>

      {/* Countries + Cities + Referrers */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Countries */}
        <div
          data-testid="visitors-countries"
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <Globe size={20} weight="fill" className="text-blue-500" />
            <div
              className="text-lg font-black text-slate-900 dark:text-white"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              Top Countries
            </div>
          </div>
          {(data?.countries || []).length === 0 && (
            <div className="text-sm text-slate-500">No visits yet in this period.</div>
          )}
          <div className="space-y-2">
            {(data?.countries || []).map((c) => {
              const pct = (c.count / maxCountry) * 100;
              return (
                <div
                  key={c.country + c.code}
                  data-testid={`country-row-${c.code}`}
                  className="relative rounded-lg bg-slate-50 dark:bg-slate-800 overflow-hidden"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-blue-100 dark:bg-blue-900/40"
                    style={{ width: `${pct}%` }}
                  />
                  <div className="relative flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg">{flagFromCode(c.code)}</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {c.country}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-500">
                        {c.unique} unique
                      </span>
                      <span className="font-black text-slate-900 dark:text-white">{c.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cities */}
        <div
          data-testid="visitors-cities"
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={20} weight="fill" className="text-emerald-500" />
            <div
              className="text-lg font-black text-slate-900 dark:text-white"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              Top Cities
            </div>
          </div>
          {(data?.cities || []).length === 0 && (
            <div className="text-sm text-slate-500">No city data yet.</div>
          )}
          <div className="space-y-2">
            {(data?.cities || []).map((c, i) => (
              <div
                key={i}
                data-testid={`city-row-${i}`}
                className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base">{flagFromCode(c.code)}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                      {c.city}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">
                      {c.country}
                    </div>
                  </div>
                </div>
                <div className="text-xs font-black text-slate-900 dark:text-white">{c.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Referrers */}
        <div
          data-testid="visitors-referrers"
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon size={20} weight="fill" className="text-purple-500" />
            <div
              className="text-lg font-black text-slate-900 dark:text-white"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              Top Referrers
            </div>
          </div>
          {(data?.referrers || []).length === 0 && (
            <div className="text-sm text-slate-500">No referrers yet.</div>
          )}
          <div className="space-y-2">
            {(data?.referrers || []).map((r, i) => (
              <div
                key={i}
                data-testid={`referrer-row-${i}`}
                className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2"
              >
                <div className="text-xs text-slate-700 dark:text-slate-200 truncate max-w-[220px]" title={r.referrer}>
                  {r.referrer}
                </div>
                <div className="text-xs font-black text-slate-900 dark:text-white">{r.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Signup traffic sources + Recent signups */}
      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <div data-testid="signup-sources-card" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-2 mb-4">
            <SignIn size={20} weight="fill" className="text-yellow-500" />
            <div className="text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              Signup Traffic Sources
            </div>
          </div>
          {(data?.signup_sources || []).length === 0 && (
            <div className="text-sm text-slate-500">No signups in this period yet.</div>
          )}
          <div className="space-y-2">
            {(() => {
              const rows = data?.signup_sources || [];
              const maxCnt = Math.max(1, ...rows.map((r) => r.count));
              return rows.map((r, i) => {
                const pct = (r.count / maxCnt) * 100;
                return (
                  <div key={i} data-testid={`signup-source-row-${i}`} className="relative rounded-lg bg-slate-50 dark:bg-slate-800 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-yellow-100 dark:bg-yellow-900/30" style={{ width: `${pct}%` }} />
                    <div className="relative flex items-center justify-between px-3 py-2">
                      <div className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-[280px]" title={r.source || "direct"}>
                        {r.source || "direct"}
                      </div>
                      <div className="text-xs font-black text-slate-900 dark:text-white">{r.count}</div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <div data-testid="recent-signups-card" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={20} weight="fill" className="text-blue-500" />
            <div className="text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              Recent Signups
            </div>
          </div>
          {(data?.recent_signups || []).length === 0 && (
            <div className="text-sm text-slate-500">No signups in this period yet.</div>
          )}
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {(data?.recent_signups || []).map((u) => (
              <RouterLink
                key={u.id}
                to={`/u/${u.id}`}
                data-testid={`recent-signup-${u.username}`}
                className="flex items-center gap-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-2 transition-colors duration-200"
              >
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-black shrink-0">
                  {(u.username || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black text-slate-900 dark:text-white truncate">@{u.username}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    from <span className="font-semibold text-slate-700 dark:text-slate-200">{u.signup_source || "direct"}</span>
                    {u.signup_referrer ? <span className="text-slate-400"> · {u.signup_referrer}</span> : null}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 shrink-0">{new Date(u.created_at).toLocaleDateString()}</div>
              </RouterLink>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Kpi({ testid, icon: Icon, color, label, value }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
    >
      <Icon size={22} weight="fill" className={color} />
      <div
        className="text-3xl font-black tracking-tight text-slate-900 dark:text-white mt-2"
        style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
      >
        {value != null ? Number(value).toLocaleString() : "—"}
      </div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}
